import Head from 'next/head';
import globalMeta, { GlobalMeta } from 'types/globalMetadata';

// Define the SeoObject type (from createBookSeo)
export interface SeoObject {
  title: string;
  slug: string;
  description: string;
  ogTitle: string;
  img: string;
}

// Define the props interface for the SEOHeader component
interface SEOHeaderProps extends Partial<SeoObject> {
  url?: string; // Full URL (overrides slug if provided)
  ogType?: string; // Open Graph type (e.g., 'website', 'article')
  imgUrl?: string; // Alternative image URL (overrides img if provided)
  imgAlt?: string; // Alt text for the image
  keywords?: string; // SEO keywords
}

// Utility function to construct the full URL
const getFullUrl = (slug: string | undefined, url: string | undefined, siteUrl: string): string => {
  if (url) return url;
  if (slug) return `${siteUrl}${slug}`;
  return siteUrl;
};

// Utility function to determine the image URL
const getImageUrl = (img: string | undefined, imgUrl: string | undefined, defaultImg: string): string => {
  if (img) return img;
  if (imgUrl) return imgUrl;
  return defaultImg;
};

/**
 * SEOHeader component for setting SEO metadata in the <head> of the page.
 * @param props - Props to customize SEO metadata, with defaults from globalMeta.
 * @returns JSX element containing SEO metadata.
 */
const SEOHeader: React.FC<SEOHeaderProps> = ({
  title = globalMeta.siteName,
  description = globalMeta.description,
  url,
  slug,
  ogType = globalMeta.type,
  ogTitle = globalMeta.siteName,
  img = '',
  imgUrl = '',
  imgAlt = '',
  keywords = '',
}) => {
  const fullUrl = getFullUrl(slug, url, globalMeta.siteUrl);
  const imageUrl = getImageUrl(img, imgUrl, globalMeta.ogImg);

  return (
    <Head>
      {/* Basic Metadata */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="icon" href="/favicon.ico" />

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph Metadata */}
      <meta property="og:title" content={ogTitle} />
      <meta property="og:locale" content="pl_PL" />
      <meta property="og:site_name" content={globalMeta.siteName} />
      <meta property="og:type" content={ogType} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:url" content={fullUrl} />

      {/* Twitter Card Metadata */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:domain" content={globalMeta.siteName} />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      {imgAlt && <meta name="twitter:image:alt" content={imgAlt} />}
    </Head>
  );
};

export default SEOHeader;